import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { formatApiError } from "../../lib/api";
import { useSeo } from "../../lib/seo";
import { toast } from "sonner";

export default function AdminLogin() {
  const { login, admin, checking } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(() => {
    try {
      if (sessionStorage.getItem("lecis_session_expired")) {
        sessionStorage.removeItem("lecis_session_expired");
        return "Session expired. Please log in again.";
      }
    } catch {}
    return "";
  });
  const navigate = useNavigate();
  useSeo({ title: "Admin · Lecis Auto Glass", description: "Lecis admin panel.", robots: "noindex,nofollow" });

  if (checking) return <div className="min-h-screen flex items-center justify-center text-zinc-500">Loading…</div>;
  if (admin) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(""); setNotice("");
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/admin");
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-100 flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="w-full max-w-md bg-white border border-zinc-200 rounded-md shadow-sm p-10">
        <div className="mb-8">
          <div className="font-display text-2xl tracking-tighter font-bold mb-1">LECIS<span className="text-red-600">.</span></div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Admin Panel</div>
        </div>
        <h1 className="font-display text-3xl tracking-tighter mb-1">Sign in</h1>
        <p className="text-sm text-zinc-500 mb-8">Authenticate to manage products, inventory, and orders.</p>

        {notice && (
          <div className="mb-6 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2" data-testid="admin-login-notice">
            {notice}
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-2 block">Email / Username</label>
            <input
              data-testid="admin-login-email"
              type="text"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full h-11 px-3 border border-zinc-300 focus:border-zinc-900 focus:outline-none text-sm bg-white"
              placeholder="admin@lecisautoglass.com or username"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-2 block">Password</label>
            <input
              data-testid="admin-login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full h-11 px-3 border border-zinc-300 focus:border-zinc-900 focus:outline-none text-sm bg-white"
            />
          </div>

          {error && <div className="text-sm text-red-600" data-testid="admin-login-error">{error}</div>}

          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="w-full h-11 bg-zinc-900 hover:bg-zinc-800 text-white font-medium text-sm tracking-wide disabled:opacity-50"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
